/* Custom Utility Function */

// Function to check if a product is present in the cart based on cart items and product handle
export function isProductPresentInCart(items, handle) {
  const handleLower = handle.toLowerCase();
  for (let i = 0; i < items.length; i++) {
    if (items[i].handle.toLowerCase() === handleLower) {
      return { isPresent: true, line: i + 1, variantId: items[i].variant_id };
    }
  }
  return { isPresent: false, line: 0 };
}

// Function to scroll to a specific section when clicked
export function scrollToSection(section) {
  document.querySelector(section).scrollIntoView({
    behavior: 'smooth', block: 'nearest', inline: 'start'
  });
}

/*
Function to set a cookie with an expiration date
@param {string} name - Name of the cookie
@param {string} value - Value to be stored in the cookie
@param {number} days - Number of days until the cookie expires
@return {void}
*/
export function setCookie(name, value, days) {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (typeof days === 'number') {
    const expirationDate = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    cookieString += `;expires=${expirationDate.toUTCString()}`;
  }

  // Set the path to `/` to make the cookie accessible throughout the website
  cookieString += `;path=/`;
  
  // Optionally add SameSite policy (adjust if needed)
  cookieString += `;SameSite=Lax`;
  
  document.cookie = cookieString;
}

/**
 * Function to check if a cookie with a given name exists
 * @param {string} name - Name of the cookie to check
 * @return {boolean} - Returns true if the cookie exists, otherwise false
 */
export function checkCookie(name) {
  const cookieString = `${encodeURIComponent(name)}=`; // Encode the name for consistency
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.indexOf(cookieString) === 0) {
      return true; // Cookie found
    }
  }
  
  return false; // Cookie not found
}


/*
Function to remove a cookie by setting its expiration date to the past
@param {string} name - Name of the cookie to be removed
@return {void}
*/
export function removeCookie(name) {
  document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; path=/;`;
}

/*
Function to remove all cookies that match a specific key substring
@param {string} substring - Substring to match the beginning of cookie keys
@return {void}
*/
export function removeCookiesByKey(str) {
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const [key] = cookie.trim().split('=');
    if (key.includes(str)) {
      document.cookie = `${key}=; Max-Age=0`;
    }
  });
}

/*
Function to find the anchor element's parent in the DOM hierarchy
@param {Element} element - Child element to start the search from
@return {Element|null} - Parent anchor element, or null if not found
*/
export function findAnchorParent(element) {
  let parent = element.parentElement;
  while (parent && parent.tagName !== 'A') {
    parent = parent.parentElement;
  }
  return parent;
}

/*
Function to update the character count label based on the input element's value length
@param {string} elementId - ID of the input element to monitor
@param {number} maxLength - Maximum character count allowed
@return {void}
*/
export function updateCharacterCount(elementId, maxLength) {
  const inputElement = document.getElementById(elementId);
  const charCountLabel = document.getElementById('charCount_' + inputElement.id);

  inputElement.addEventListener('input', () => {
    let maxLen = maxLength ? Number(maxLength) : null;

    if (maxLen) {
      if (inputElement.value.length > maxLen) {
        inputElement.value = inputElement.value.slice(0, maxLen);
      }

      if (charCountLabel) {
        charCountLabel.textContent = inputElement.value.length + '/' + maxLen;
      }

      // Toggle visibility of charCountLabel based on inputElement value length
      if (inputElement.value.length === 0 && charCountLabel) {
        charCountLabel.style.display = 'none';
      } else {
        charCountLabel.style.display = 'flex';
      }
    }
  });

  // Initial check to hide label if inputElement value is initially empty
  if (inputElement.value.length === 0 && charCountLabel) {
    charCountLabel.style.display = 'none';
  }
}

/**
 * Creates a debounced version of a function that delays its execution until after a specified wait time
 * has elapsed since the last time the debounced function was invoked.
 * 
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay; defaults to 300ms
 * @return {Function} - A new debounced function
 */
 
export function debounce(func, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

/**
 * Deletes specified keys from an object.
 *
 * @param {object} obj - The object to modify.
 * @param {string[]} keysToDelete - An array of keys to delete.
 */
export function deleteKeys(obj, keysToDelete) {
  keysToDelete.forEach(key => {
    delete obj[key];
  });
}

/**
 * Deletes specified key from an object.
 *
 * @param {object} obj - The object to modify.
 * @param {string} key - key to delete.
 */
export function deleteKey(obj, key) {
  delete obj[key];
}

/**
 * Finds all keys associated with a given value in an object.
 *
 * @param {object} obj - The object to search.
 * @param {*} value - The value to find.
 * @returns {string[]} - An array of keys associated with the value.
 */
export function getKeysByValue(obj, value) {
  return Object.keys(obj).filter(key => obj[key] === value);
}


/**
 * Formats a given Date object into a string in the format DD-MM-YYYY.
 * @param {Date} date - The Date object to be formatted.
 * @returns {string} The formatted date in the format DD-MM-YYYY.
 */
export function formatDateToDDMMYYYY(date) {
  return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
}
